import * as React from 'react'
import { Search, X } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { FilterDropdown } from '@/components/ui/filter-dropdown'
import { Input } from '@/components/ui/input'
import {
  SortableTableHead,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { deleteJSON, getJSON, postJSON } from '@/lib/api'
import { displayDateTime } from '@/lib/dates'

interface ChapterMember {
  id: number
  name: string
  email: string | null
  kind: 'person' | 'organization'
  status: string
  added_at: string
}
interface ContactsResponse {
  contacts: Array<{ id: number; name: string; email: string | null }>
}
const statuses = ['Active', 'Lapsed', 'Suspended', 'Prospect', 'Organization'].map((value) => ({
  value,
  label: value,
}))

export function ChapterMembersSection({
  chapterId,
  readOnly,
}: {
  chapterId: number
  readOnly: boolean
}) {
  const state = useChapterMembersState(chapterId)
  const {
    search,
    setSearch,
    status,
    setStatus,
    sort,
    direction,
    addOpen,
    setAddOpen,
    contactSearch,
    setContactSearch,
    removeTarget,
    setRemoveTarget,
    membersQuery,
    contactsQuery,
    add,
    remove,
    members,
    toggleSort,
  } = state

  return (
    <section className="px-4 py-4">
      <div className="flex items-center justify-between gap-3 pb-3">
        <h3 className="text-body font-medium">Members · {members.length}</h3>
        {!readOnly && (
          <Button variant="outline" size="sm" onClick={() => setAddOpen(true)}>
            + Add member
          </Button>
        )}
      </div>
      <div className="flex items-center gap-2 pb-3">
        <div className="relative w-64">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            className="h-9 pl-8"
            placeholder="Search name or email"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <FilterDropdown
          label="Member status"
          emptyLabel="Member status"
          options={statuses}
          selected={status}
          onChange={setStatus}
        />
      </div>
      {membersQuery.isLoading ? (
        <p className="text-body-sm text-muted-foreground">Loading members…</p>
      ) : membersQuery.isError ? (
        <Alert>Unable to load chapter members.</Alert>
      ) : members.length === 0 ? (
        <EmptyState
          variant="quiet"
          className="items-start px-0 text-left"
          title="No one is tagged to this chapter yet."
          action={
            !readOnly ? (
              <Button size="sm" onClick={() => setAddOpen(true)}>
                Add member
              </Button>
            ) : undefined
          }
        />
      ) : (
        <MemberTable
          members={members}
          sort={sort}
          direction={direction}
          onSort={toggleSort}
          readOnly={readOnly}
          onRemove={setRemoveTarget}
        />
      )}
      <AddMemberDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        search={contactSearch}
        onSearch={setContactSearch}
        contacts={contactsQuery.data?.contacts ?? []}
        onAdd={(id) => add.mutate(id)}
      />
      <RemoveMemberDialog
        member={removeTarget}
        onCancel={() => setRemoveTarget(null)}
        onRemove={() => removeTarget && remove.mutate(removeTarget.id)}
      />
    </section>
  )
}

function useChapterMembersState(chapterId: number) {
  const client = useQueryClient()
  const [search, setSearch] = React.useState('')
  const [status, setStatus] = React.useState<ReadonlySet<string>>(new Set())
  const [sort, setSort] = React.useState<'name' | 'member_status'>('name')
  const [direction, setDirection] = React.useState<'asc' | 'desc'>('asc')
  const [addOpen, setAddOpen] = React.useState(false)
  const [contactSearch, setContactSearch] = React.useState('')
  const [removeTarget, setRemoveTarget] = React.useState<ChapterMember | null>(null)
  const statusValue = status.size === 1 ? [...status][0] : undefined
  const membersQuery = useQuery({
    queryKey: ['chapter-members', chapterId, search, statusValue, sort, direction],
    queryFn: () =>
      getJSON<{ members: ChapterMember[] }>(
        memberPath(chapterId, { search, status: statusValue, sort, direction }),
      ),
    retry: false,
  })
  const contactsQuery = useQuery({
    queryKey: ['chapter-member-candidates', contactSearch],
    queryFn: () =>
      getJSON<ContactsResponse>(
        `/api/v1/contacts?${new URLSearchParams({ search: contactSearch })}`,
      ),
    enabled: addOpen,
    retry: false,
  })
  const add = useMutation({
    mutationFn: (contactId: number) =>
      postJSON(`/api/v1/contacts/${contactId}/chapters`, { chapter_id: chapterId }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['chapter-members', chapterId] })
      setAddOpen(false)
      setContactSearch('')
    },
  })
  const remove = useMutation({
    mutationFn: (contactId: number) =>
      deleteJSON(`/api/v1/contacts/${contactId}/chapters/${chapterId}`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['chapter-members', chapterId] })
      setRemoveTarget(null)
    },
  })
  const members = membersQuery.data?.members ?? []
  const toggleSort = (key: 'name' | 'member_status') => {
    if (sort === key) setDirection((value) => (value === 'asc' ? 'desc' : 'asc'))
    else {
      setSort(key)
      setDirection('asc')
    }
  }
  return {
    search,
    setSearch,
    status,
    setStatus,
    sort,
    direction,
    addOpen,
    setAddOpen,
    contactSearch,
    setContactSearch,
    removeTarget,
    setRemoveTarget,
    membersQuery,
    contactsQuery,
    add,
    remove,
    members,
    toggleSort,
  }
}

function MemberTable({
  members,
  sort,
  direction,
  onSort,
  readOnly,
  onRemove,
}: {
  members: ChapterMember[]
  sort: 'name' | 'member_status'
  direction: 'asc' | 'desc'
  onSort: (key: 'name' | 'member_status') => void
  readOnly: boolean
  onRemove: (member: ChapterMember) => void
}) {
  const heading = (key: 'name' | 'member_status', label: string) => (
    <SortableTableHead
      active={sort === key}
      direction={direction === 'asc' ? 'ascending' : 'descending'}
      onToggle={() => onSort(key)}
    >
      {label}
    </SortableTableHead>
  )
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {heading('name', 'Person')}
          {heading('member_status', 'Status')}
          <TableHead>Email</TableHead>
          <TableHead>Added</TableHead>
          <TableHead className="w-12" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {members.map((member) => (
          <TableRow key={member.id}>
            <TableCell>
              {member.name}
              {member.kind === 'organization' && (
                <Badge variant="organization" className="ml-2">
                  Organization
                </Badge>
              )}
            </TableCell>
            <TableCell>
              <Badge
                variant={
                  member.status === 'Active'
                    ? 'success'
                    : member.status === 'Lapsed'
                      ? 'warning'
                      : 'neutral'
                }
              >
                {member.status}
              </Badge>
            </TableCell>
            <TableCell className="text-muted-foreground">{member.email || '—'}</TableCell>
            <TableCell className="text-muted-foreground">
              {displayDateTime(member.added_at)}
            </TableCell>
            <TableCell className="w-12 py-0">
              {!readOnly && (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove ${member.name} from chapter`}
                  onClick={() => onRemove(member)}
                >
                  <X className="size-4" aria-hidden="true" />
                </Button>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function AddMemberDialog({
  open,
  onOpenChange,
  search,
  onSearch,
  contacts,
  onAdd,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  search: string
  onSearch: (value: string) => void
  contacts: ContactsResponse['contacts']
  onAdd: (id: number) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add member</DialogTitle>
        </DialogHeader>
        <Input
          placeholder="Search contacts by name or email"
          value={search}
          onChange={(event) => onSearch(event.target.value)}
        />
        <div className="max-h-64 divide-y divide-border overflow-auto">
          {contacts.map((contact) => (
            <Button
              variant="ghost"
              className="flex h-auto w-full items-center justify-between rounded-none py-2 text-left text-body-sm"
              key={contact.id}
              onClick={() => onAdd(contact.id)}
            >
              <span>{contact.name}</span>
              <span className="text-caption text-muted-foreground">
                {contact.email || 'Organization'}
              </span>
            </Button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function RemoveMemberDialog({
  member,
  onCancel,
  onRemove,
}: {
  member: ChapterMember | null
  onCancel: () => void
  onRemove: () => void
}) {
  return (
    <Dialog open={member !== null} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Remove {member?.name} from this chapter?</DialogTitle>
        </DialogHeader>
        <p className="text-body-sm">This removes the chapter tag from the contact record too.</p>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onRemove}>
            Remove member
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function memberPath(chapterId: number, params: Record<string, string | undefined>) {
  const query = new URLSearchParams(
    Object.entries(params).filter((entry): entry is [string, string] => Boolean(entry[1])),
  )
  return `/api/v1/chapters/${chapterId}/members?${query.toString()}`
}
